/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import {selectExperiment, selectGreenlineIdToPlotData, selectGreenlineIdToProperties} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign"
import {useAppSelector} from "../../../../../../redux/hooks"
import React, {useEffect, useMemo, useState} from "react"
import Plot from "react-plotly.js"
import {ELineTypes, idLineColorsMap, idToLabelLineTypesMap} from "../../../../vehiclePrograms/ItemDetails/Tabs/SensingSysPerfTab/constants"
import ArgosCheckbox from "../../../../../components/Form/Checkbox/ArgosCheckbox"
import {PlotData} from "../../../../../components/Reports/constants"
import {SimpleTreeView} from "@mui/x-tree-view/SimpleTreeView"
import {TreeItem} from "@mui/x-tree-view"
import {ArgosCollapseNodeIcon, ArgosExpandNodeIcon} from "../../../../../components/Icons/ArgosTreeIcons"
import {calculateRangeWithBuffer, getMaxFinite} from "../../../../../../helpers/graphs";
import ArgosButton, {ArgosLoadingButton} from "../../../../../components/Buttons/ArgosButton"
import {SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS} from "../../../constants"
import {usePostRequest} from "../../../../../../api/helper"
import toast from "react-hot-toast"
import VAR_COMMON_CONSTANTS from "../../../../../../constants/globalVariables";
import { green } from "@mui/material/colors"


/**
 * Controls the state of the plot tree.
 * @typedef {Object} PlotMode
 * @property {string} edit - Edit mode
 * @property {string} view - View mode
 */
type PlotMode = 'edit' | 'view'

const MAX_OBJECTS_TO_PLOT = Infinity  /** The number of objects that can be selected at one time for plotting */

/**
 * Merges the green line ID to plot data together into one structure.
 * @param greenlineIdToPlotData
 */
function mergeGreenlineIdToPlotData(greenlineIdToPlotData: any) {
    const mergedData: any = {
        objectNames: {},
        plot: {}
    };

    for (const greenlineId in greenlineIdToPlotData) {
        const data = greenlineIdToPlotData[greenlineId];

        mergedData.plotTitle = data.plotTitle;
        mergedData.xAxisLabel = data.xAxisLabel;
        mergedData.yAxisLabel = data.yAxisLabel;

        for (const objectId in data.objectNames) {
            mergedData.objectNames[objectId] = data.objectNames[objectId];
        }

        if (!mergedData.plot[greenlineId]) {
            mergedData.plot[greenlineId] = {};
        }

        mergedData.plot[greenlineId] = data.plot[greenlineId];
    }

    return mergedData;
}
/**
 * Handles the change of children in the tree structure.
 * It toggles the selection of all children nodes under a given parent node.
 * This function updates the selected lines state based on the current selection of children nodes.
 * It checks if all children are selected, and if so, it unselects them.
 * If not all children are selected, it selects all of them.
 * It also cleans up empty arrays and objects in the selected lines state.
 * @param greenLineId
 * @param key
 * @param node
 * @param selectedLines
 * @param setSelectedLines
 */
export const handleChildrenChange = (
  greenLineId: string,
  key: string,
  node: Record<string, unknown>,
  selectedLines: Record<string, Record<string, string[]>>,
  setSelectedLines: React.Dispatch<React.SetStateAction<Record<string, Record<string, string[]>>>>
): void => {
  const childrenKeys = Object.keys(node)
  const allSelected = childrenKeys.every(childKey =>
    selectedLines?.[greenLineId] &&
    selectedLines?.[greenLineId]?.[key] &&
    selectedLines?.[greenLineId]?.[key]?.includes(childKey)
  )
  const updatedSelectedLines: Record<string, Record<string, string[]>> = {...selectedLines}
  if (!updatedSelectedLines[greenLineId]) {
    updatedSelectedLines[greenLineId] = {}
  }
  if (!updatedSelectedLines[greenLineId][key]) {
    updatedSelectedLines[greenLineId][key] = []
  }
  if (allSelected) {
    // Unselect all children
    updatedSelectedLines[greenLineId][key] = updatedSelectedLines[greenLineId][key].filter(
      (id: string) => !childrenKeys.includes(id)
    )
  } else {
    // Select all children
    const current = updatedSelectedLines[greenLineId][key]
    updatedSelectedLines[greenLineId][key] = Array.from(new Set([...current, ...childrenKeys]))
  }
  // Clean up empty arrays/objects
  if (updatedSelectedLines[greenLineId][key].length === 0) {
    delete updatedSelectedLines[greenLineId][key]
  }
  if (Object.keys(updatedSelectedLines[greenLineId]).length === 0) {
    delete updatedSelectedLines[greenLineId]
  }
  setSelectedLines(updatedSelectedLines)
}
/**
 * Main component for Analysis Results Plot. Analysis Results Plot shows selected
 * red line, raw data, green line data with a Plotly plot.
 * @returns {JSX.Element} The rendered component.
 */
export const AnalysisResultsPlot = () => {
  const experiment = useAppSelector(selectExperiment)
  const experimentId = experiment?.id
  const studyId = experiment?.studyId
  const greenlineIdToPlotData = useAppSelector(selectGreenlineIdToPlotData)
  const plotData = useMemo(() => mergeGreenlineIdToPlotData(greenlineIdToPlotData), [greenlineIdToPlotData])

  const [selectedLines, setSelectedLines] = useState<{
    [greenLineDataSetId: string]: {
      [lineId: string]: string[]
    }
  }>({})
  const [selectedPlotData, setSelectedPlotData] = useState<any>([])
  const [configObject, setConfigObject] = useState<any>({})
  const [globalMin, setGlobalMin] = useState<number | null>(null)
  const [globalMax, setGlobalMax] = useState<number | null>(null)
  const [plotMode, setPlotMode] = useState<PlotMode>('edit')
  const [isLoadingPlotData, setIsLoadingPlotData] = useState<boolean>(false)

  const fetchPlotDataUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.fetchPlotData
  const fetchPlotDataHandle = usePostRequest(fetchPlotDataUrl)

  /**
   * Fetches plot data based on the selected lines and updates the plot mode.
   */
  const handleFetchPlotData = (plotMode: PlotMode) => {
    if (!experimentId || !studyId) return
    let body
    if (plotMode === 'edit') {
      body = {experimentId, studyId}
    } else {
      body = {experimentId, studyId, selectedLines}
    }
    setIsLoadingPlotData(true)
    fetchPlotDataHandle(body).then(() => {
      setPlotMode(plotMode)
    }).catch((err) => {
      console.log(err)
      toast.error("An error occurred while fetching the plot data")
    }).finally(() => {
      setIsLoadingPlotData(false)
    })
  }

  const handleFetchTreeData = () => {
    if (!experimentId || !studyId) return
    const body = {experimentId, studyId}
    setIsLoadingPlotData(true)
    fetchPlotDataHandle(body).then(() => {
      setPlotMode('edit')
    }).catch((err) => {
      console.log(err)
      toast.error("An error occurred while fetching the plot data")
    }).finally(() => {
      setIsLoadingPlotData(false)
    })
  }
  useEffect(() => {
    handleFetchTreeData()
  }, [experimentId, studyId]);

  /**
   * Changes the selection mode to 'edit' and clears the selected plot data.
   */
  const handleChangeSelection = () => {
    setPlotMode('edit')
  }

  /** Update selected plot data when selected objects and lines change */
  useEffect(() => {
    constructPlottingObject()
    constructConfigObject()
  }, [plotData, selectedLines, plotMode])

  /**
   * Converts line data to Plotly format and updates the selected plot data.
   */
  const constructPlottingObject = () => {
    if (!plotData || plotMode === 'edit') return
    let newPlotData: any = []
    let max: any = null
    Object.keys(selectedLines).forEach((greenLineId) => {
      const lineTypes = selectedLines[greenLineId]
      Object.keys(lineTypes).forEach((lineType) => {
        const objects = lineTypes[lineType]
        const data: PlotData = plotData.plot?.[greenLineId]?.[lineType]
        if(!data) return
        objects.forEach((object) => {
          const data: PlotData = plotData.plot[greenLineId][lineType][object]
          if (data && data.xData && data.yData) {
            let _x = data.xData.map((element) => Math.abs(element))
            let _y = data.yData.map((element) => Math.abs(element))
            if (_x != null && _y != null) {
              let absoluteMax = getMaxFinite(_x.concat(_y));
              if (max == null || absoluteMax > max)
                max = absoluteMax
            }
            const _color = idLineColorsMap?.[data.line] ?? "black"
            newPlotData.push({
              x: data.xData,
              y: data.yData,
              type: data.graphType,
              mode: data.graphMode,
              marker: {color: _color}
            })
          }
        })
      })
    })
    setGlobalMin((-1) * max)
    setGlobalMax(max)
    setSelectedPlotData(newPlotData)
  }

  /**
   * Forces a component update.
   * @returns {Function} Function to force update the component.
   */
  const useForceUpdate = () => {
    const [value, setValue] = useState(0)
    return () => setValue(value => value + 1)
  }

  const forceUpdate = useForceUpdate()
  const handleClick = () => {
    forceUpdate()
  }

  /**
   * Constructs the configuration object for the Plotly plot.
   */
  const constructConfigObject = () => {
    let _stuff = []
    const configObj = {
      name: 'Square Plot',
      icon: {
        width: 80,
        height: 80,
        path: 'M78,22V79H21V22H78m9-9H12V88H87V13ZM68,46.22H31V54H68ZM53,32H45.22V69H53Z',
        transform: 'matrix(1 0 0 1 -10 -10)'

      },
      click: () => handleClick(),
    }
    _stuff.push(configObj)
    setConfigObject({'modeBarButtonsToAdd': _stuff})
  }

  /**
   * Computes the total number of objects selected for every green line data set
   * and line type
   * @param {Object} data - The selected lines data.
   * @returns {number} The total number of objects selected.
   */
  const computeNumOfObjectsSelected = (data: { [greenLineDataSetId: string]: { [lineId: string]: string[] } }): number => {
    let totalObjectsSelected = 0
    Object.keys(data).forEach(greenLineDataSetId => {
      Object.keys(data[greenLineDataSetId]).forEach(lineId => {
        totalObjectsSelected += data[greenLineDataSetId][lineId].length
      })
    })
    return totalObjectsSelected
  }

  /**
   * Updates the selected line state when toggling the tree item checkbox.
   * @param {Event} event - The event object.
   * @param {string} greenLineId - The green line ID.
   * @param {string} lineId - The line ID.
   * @param {string} objectId - The object ID.
   */
  const handleObjectChange = (event: any, greenLineId: string, lineId: string, objectId: string) => {
    const _selectedLineTypes = {...selectedLines}
    if (!_selectedLineTypes[greenLineId]) {
      _selectedLineTypes[greenLineId] = {}
    }
    if (!_selectedLineTypes[greenLineId][lineId]) {
      _selectedLineTypes[greenLineId][lineId] = []
    }
    const objectIdIndex = _selectedLineTypes[greenLineId][lineId].indexOf(objectId)
    if (objectIdIndex !== -1) {
      _selectedLineTypes[greenLineId][lineId].splice(objectIdIndex, 1)
    } else {
      _selectedLineTypes[greenLineId][lineId].push(objectId)
    }
    if (_selectedLineTypes[greenLineId][lineId].length === 0) {
      delete _selectedLineTypes[greenLineId][lineId]
    }
    if (Object.keys(_selectedLineTypes[greenLineId]).length === 0) {
      delete _selectedLineTypes[greenLineId]
    }
    setSelectedLines(_selectedLineTypes)
  }

  const greenlineIdToProperties = useAppSelector(selectGreenlineIdToProperties)

  /**
   * Renders the tree structure for the plot data.
   * @param {Object} nodes - The nodes of the tree.
   * @param {number} [level=0] - The current level of the tree.
   * @param {string} [greenLineId=""] - The green line ID.
   * @param {string} [lineId=""] - The line ID.
   * @returns {JSX.Element|null} The rendered tree structure.
   */
  const renderTree = (nodes: any, level: number = 0, greenLineId: string = "", lineId: string = "") => {
    if (!nodes || level > 2) return null

    return Object.keys(nodes).map((key) => {
      const node = nodes[key]
      const hasChildren = typeof node === 'object' && !Array.isArray(node)
      const treeItemLabel = (() => {
        if (level === 0) {
          const greenlineName = greenlineIdToProperties?.[key]?.name
          return greenlineName ?? key
        } else if (level === 1) {
          return idToLabelLineTypesMap?.[key as ELineTypes] ?? key
        } else if (level === 2) {
          return plotData.objectNames?.[key] ?? key
        }
        return key
      })()
      const isChecked = (() => {
        if (!node) return false
        if (level === 0) {
          const childrenKeys = Object.keys(node)
          const selectedChildren = childrenKeys?.filter(childKey => selectedLines[key] && Object.keys(selectedLines[key])?.includes(childKey))
          return selectedChildren.length === childrenKeys.length
        } else if (level === 1) {
          const childrenKeys = Object.keys(node)
          const selectedChildren = childrenKeys?.filter(childKey => selectedLines[greenLineId] && selectedLines[greenLineId][key]?.includes(childKey))
          return selectedChildren.length === childrenKeys.length
        } else if (level === 2) {
          return selectedLines[greenLineId]?.[lineId] && Boolean(selectedLines[greenLineId][lineId]?.includes(key))
        }
        return false
      })()
      const isIndeterminate = (() => {
        if (!node) return false
        if (level === 0) {
          const childrenKeys = Object.keys(node)
          const selectedChildren = childrenKeys?.filter(childKey => selectedLines[key] && Object.keys(selectedLines[key])?.includes(childKey))
          return selectedChildren.length > 0 && selectedChildren.length < childrenKeys.length
        } else if (level === 1) {
          const childrenKeys = Object.keys(node)
          const selectedChildren = childrenKeys?.filter(childKey => selectedLines[greenLineId] && selectedLines[greenLineId][key]?.includes(childKey))
          return selectedChildren.length > 0 && selectedChildren.length < childrenKeys.length
        }
        return false
      })()
      const isDisabled = (() => {
        if (level === 0) {
          return true  /** Level 0 nodes are always disabled */
        } else if (level === 2 || level === 1) {
          if (plotMode === 'edit') {
            /**
             * Each level 2 node could be enabled or disabled in 'edit' mode,
             * depending on whether fetching plot data is loading
             */
            if (isLoadingPlotData) {
              return true  /** Disable all Level 2 nodes when loading plot data */
            } else {
              const objectsSelected = computeNumOfObjectsSelected(selectedLines)
              if (objectsSelected >= MAX_OBJECTS_TO_PLOT) {
                if (!selectedLines[greenLineId]?.[lineId]?.includes(key)) {
                  return true  /** Disable all Level 2 nodes that aren't part of the selection */
                }
              }
              return false  /** Enable all Level 2 nodes under all other conditions */
            }
          } else if (plotMode === 'view') {
            /**
             * Each level 2 node could be enabled or disabled in 'view' mode,
             * depending on presence of plot data
             */
            const data: PlotData = plotData.plot?.[greenLineId]?.[lineId]?.[key]
            if (data && data.xData && data.yData) {
              return false  /** Enable nodes that contain plot data */
            } else {
              return true  /** Disable nodes that do not contain plot data */
            }
          }
        }
        return false
      })()

      const handleChange = (event: any) => {
        /** Only Level 2 nodes are able to be clicked */
        if (level === 1) {
          handleChildrenChange(greenLineId, key, node, selectedLines, setSelectedLines)
        }
        if (level === 2) {
          handleObjectChange(event, greenLineId, lineId, key)
        }
      }
      const newGreenLineId = level === 0 ? key : greenLineId
      const newLineId = level === 1 ? key : lineId
      const labelContent = (
        <ArgosFlexbox style={{alignItems: 'center'}}>
          <ArgosCheckbox
            key={newGreenLineId + newLineId + key + isChecked}
            onChange={handleChange}
            onClick={(event) => {
              event.stopPropagation()
            }}
            checked={isChecked}
            indeterminate={isIndeterminate}
            disabled={isDisabled}
          />
          <span>{treeItemLabel}</span>
        </ArgosFlexbox>
      )

      return (
        <TreeItem
          key={newGreenLineId + newLineId + key}
          id={newGreenLineId + newLineId + key}
          label={labelContent}
          itemId={newGreenLineId + newLineId + key}
        >
          {hasChildren ? renderTree(node, level + 1, newGreenLineId, newLineId) : null}
        </TreeItem>
      )
    })
  }

  const treeContent = plotData?.plot ? renderTree(plotData.plot) : null

  const {minRange, maxRange} = calculateRangeWithBuffer(globalMin, globalMax)

  const isSelectedLinesEmpty = Object.keys(selectedLines).length === 0

  return (
    <ArgosFlexbox style={{flex: 1}}>
      <ArgosFlexbox vertical>
        <ArgosFlexbox>
          <ArgosLoadingButton
            id='view-plot-button'
            data-testid='view-plot-button'
            variant='contained'
            size='large'
            disabled={plotMode === 'view' || isSelectedLinesEmpty}
            onClick={() => handleFetchPlotData('view')}
            isLoading={isLoadingPlotData}
          >
            View Plot
          </ArgosLoadingButton>
          <ArgosButton
            id='change-selection-button'
            data-testid='change-selection-button'
            variant='contained'
            size='large'
            disabled={plotMode === 'edit'}
            onClick={handleChangeSelection}
          >
            Change Selection
          </ArgosButton>
        </ArgosFlexbox>
        <SimpleTreeView
          slots={{
            collapseIcon: ArgosExpandNodeIcon,
            expandIcon: ArgosCollapseNodeIcon,
          }}
          sx={{flexGrow: 1, overflowY: 'auto'}}
        >
          {treeContent}
        </SimpleTreeView>
      </ArgosFlexbox>
      <ArgosFlexbox style={{}}>
        <Plot
          data={selectedPlotData}
          config={configObject}
          useResizeHandler={true}
          style={{height: 700, width: 700, paddingBottom: "3px"}}
          layout={{
            annotations: [
                VAR_COMMON_CONSTANTS.plots.scatterPlot.DEFAULT_ANNOTATION
              ],
            showlegend: false,
            title: plotData?.plotTitle ?? "",
            xaxis: {
              title: plotData?.xAxisLabel ?? "",
              rangemode: "tozero",
              nticks: 6,
              range: [minRange, maxRange]
            },
            yaxis: {
              title: plotData?.yAxisLabel ?? "",
              rangemode: "tozero",
              nticks: 6,
              range: [minRange, maxRange],
            },
          }}
        />
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
}

